import React, { useCallback, useMemo, useState } from "react";
import { Button, DatePicker, Input, Select, Table, Tag, message } from "antd";
import type { TableColumnsType } from "antd";
import { AlertTriangle, Check, ChevronDown, ChevronUp, Download } from "lucide-react";
import dayjs from "dayjs";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import TestingGuide from "components/testing-guide/testing-guide";

import { useGetJurisdictionTypesQuery } from "graphql/queries/get-jurisdiction-types.generated";
import { useGetLayerAuthoritiesQuery } from "graphql/queries/get-layer-authorities.generated";
import { useImportJurisdictionBoundariesMutation } from "graphql/mutations/import-jurisdiction-boundaries.generated";
import { useJurisdictionBoundaryImportsSubscription } from "graphql/subscriptions/jurisdiction-boundary-imports.generated";

import { JurisdictionBoundaryImportStatus, JurisdictionDomain, JurisdictionWorkKind } from "constants/field-values";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";

type BoundaryImport = NonNullable<
	ReturnType<typeof useJurisdictionBoundaryImportsSubscription>["data"]
>["jurisdiction_boundary_imports"][number];

const STATUS_COLOR: Record<string, string> = {
	[JurisdictionBoundaryImportStatus.DOWNLOADING]: "processing",
	[JurisdictionBoundaryImportStatus.IMPORTING]: "processing",
	[JurisdictionBoundaryImportStatus.COMPLETE]: "success",
	[JurisdictionBoundaryImportStatus.FAILED]: "error"
};

// The stored values are for the database, not for a person reading a table.
const STATUS_LABEL: Record<string, string> = {
	[JurisdictionBoundaryImportStatus.DOWNLOADING]: "Downloading the map",
	[JurisdictionBoundaryImportStatus.IMPORTING]: "Reading its areas",
	[JurisdictionBoundaryImportStatus.COMPLETE]: "Finished",
	[JurisdictionBoundaryImportStatus.FAILED]: "Failed"
};

/**
 * The desks a layer can name separately, in the order a fire marshal's layer tends
 * to list them. Only the ones a published layer actually distinguishes appear here;
 * anything a layer does not name falls back to the area's own authority.
 */
const WORK_KINDS_WITH_THEIR_OWN_AUTHORITY = [
	{ value: JurisdictionWorkKind.CONSTRUCTION_PERMIT, label: "Building permit", placeholder: "Building_Permit_AHJ" },
	{ value: JurisdictionWorkKind.SITE_REVIEW, label: "Site / subdivision", placeholder: "Subdivision_Review" },
	{
		value: JurisdictionWorkKind.OPERATIONAL_INSPECTION,
		label: "Operational inspection",
		placeholder: "Inspection_AHJ"
	},
	{ value: JurisdictionWorkKind.BURN_PERMIT, label: "Burn permit", placeholder: "Burn_Permit_AHJ" },
	{ value: JurisdictionWorkKind.FIREWORKS, label: "Fireworks", placeholder: "Firework_S" },
	{ value: JurisdictionWorkKind.MOBILE_FOOD, label: "Mobile food", placeholder: "Mobile_Food_Permit_AHJ" },
	{ value: JurisdictionWorkKind.TEMPORARY_EVENT, label: "Tent / event", placeholder: "Tent_Event_AHJ" }
];

/**
 * What one finished import actually read off the layer, and which offices it found.
 *
 * This exists because everything the loader works out — which attribute holds the
 * name, which column holds the building-permit authority — used to be invisible.
 * The only way to check a reading was to open the layer by hand, which is what leads
 * somebody to fill the override fields in with a guess. Now the reading is on screen
 * next to the run that produced it.
 *
 * The offices are the other half: a layer names authorities, and the ones that are
 * not in the registry yet are the reason an address inside them cannot be answered.
 * They are called out here rather than left for someone to discover from a failed
 * lookup weeks later.
 */
const LayerReading: React.FC<{ boundaryImport: BoundaryImport }> = ({ boundaryImport }) => {
	const { data, loading } = useGetLayerAuthoritiesQuery({
		variables: { source: boundaryImport.source },
		fetchPolicy: "cache-and-network"
	});

	const attributesRead = useMemo(() => {
		const detected: { label: string; attribute: string }[] = [];
		if (boundaryImport.name_field) detected.push({ label: "Area name", attribute: boundaryImport.name_field });
		if (boundaryImport.id_field) detected.push({ label: "Feature id", attribute: boundaryImport.id_field });
		if (boundaryImport.county_field) detected.push({ label: "County", attribute: boundaryImport.county_field });

		const authorityFields = (boundaryImport.authority_fields ?? {}) as Record<string, string>;
		for (const workKind of WORK_KINDS_WITH_THEIR_OWN_AUTHORITY) {
			const attribute = authorityFields[workKind.value];
			if (attribute) detected.push({ label: workKind.label, attribute });
		}

		return detected;
	}, [boundaryImport]);

	// One row per office the layer names, however many areas name it. What matters is
	// whether we have it on file, not how many polygons mention it.
	const officesNamed = useMemo(() => {
		const byName = new Map<string, { name: string; registered: boolean; areas: number }>();

		for (const area of data?.jurisdiction_boundaries ?? []) {
			const namesFromArea: { name: string; registered: boolean }[] = area.authorities.length
				? area.authorities.map(authority => ({
						name: authority.authority_name ?? area.name,
						registered: !!authority.jurisdiction?.id
					}))
				: [{ name: area.name, registered: !!area.jurisdiction?.id }];

			for (const office of namesFromArea) {
				const existing = byName.get(office.name);
				byName.set(office.name, {
					name: office.name,
					registered: existing ? existing.registered || office.registered : office.registered,
					areas: (existing?.areas ?? 0) + 1
				});
			}
		}

		return [...byName.values()].sort(
			(first, second) => Number(first.registered) - Number(second.registered) || first.name.localeCompare(second.name)
		);
	}, [data]);

	const unregisteredCount = officesNamed.filter(office => !office.registered).length;

	return (
		<div className="bly-reading">
			<div className="bly-reading-block">
				<span className="bly-reading-title">Which column of the map we took each value from</span>
				{attributesRead.length === 0 ? (
					<span className="bly-cell-secondary">This map was loaded before we started recording that.</span>
				) : (
					<ul className="bly-reading-list">
						{attributesRead.map(read => (
							<li key={read.label}>
								{read.label} <code>{read.attribute}</code>
							</li>
						))}
					</ul>
				)}
			</div>

			<div className="bly-reading-block">
				<span className="bly-reading-title">
					Offices this map says are in charge
					{unregisteredCount > 0 && (
						<em className="bly-reading-warn"> — {unregisteredCount} of them are not in your AHJ list yet</em>
					)}
				</span>
				{loading && officesNamed.length === 0 ? (
					<span className="bly-cell-secondary">Loading…</span>
				) : officesNamed.length === 0 ? (
					<span className="bly-cell-secondary">This map loaded no areas, so it names no offices.</span>
				) : (
					<ul className="bly-reading-list">
						{officesNamed.map(office => (
							<li key={office.name} className={office.registered ? undefined : "bly-reading-missing"}>
								{office.registered ? <Check size={13} /> : <AlertTriangle size={13} />}
								<span>{office.name}</span>
								<span className="bly-cell-secondary">
									{office.areas} {office.areas === 1 ? "area" : "areas"}
								</span>
								{!office.registered && (
									<em>not in your AHJ list — add it with this exact name, then load this map again</em>
								)}
							</li>
						))}
					</ul>
				)}
			</div>
		</div>
	);
};

const isRunning = (status: string) =>
	status === JurisdictionBoundaryImportStatus.DOWNLOADING || status === JurisdictionBoundaryImportStatus.IMPORTING;

/**
 * Loading the boundary layers the AHJ lookup tests addresses against.
 *
 * A layer is published by whoever draws the line — a city open data portal, a county
 * GIS hub — and this takes the URL it serves GeoJSON from. Until a region has one
 * loaded, the lookup there falls back to matching on the geocoded city, which works
 * but cannot tell an address inside the city limits from one in the ETJ beside it.
 *
 * Re-importing under the same layer name is the supported way to stay current: the
 * loader recognises the areas it already has and versions the ones that moved.
 */
const BoundaryLayers = () => {
	// Tells Ollie what the reader is looking at.
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen:
				"The jurisdiction boundary layers screen: the map layers that decide which authority has jurisdiction over a property."
		})
	);

	const [layerName, setLayerName] = useState("");
	const [jurisdictionTypeId, setJurisdictionTypeId] = useState<string>();
	const [layerUrl, setLayerUrl] = useState("");
	const [effectiveFrom, setEffectiveFrom] = useState<dayjs.Dayjs | null>(null);
	const [areFieldsShown, setAreFieldsShown] = useState(false);
	const [nameField, setNameField] = useState("");
	const [idField, setIdField] = useState("");
	const [countyField, setCountyField] = useState("");
	// Work kind to layer attribute, for a layer that names one authority per permit.
	const [authorityFields, setAuthorityFields] = useState<Record<string, string>>({});
	const [isStarting, setIsStarting] = useState(false);

	const { data: typesData, loading: loadingTypes } = useGetJurisdictionTypesQuery({
		fetchPolicy: "cache-first"
	});

	const { data: importsData } = useJurisdictionBoundaryImportsSubscription();
	const boundaryImports = useMemo(() => importsData?.jurisdiction_boundary_imports ?? [], [importsData]);

	const [startImport] = useImportJurisdictionBoundariesMutation();

	// "fire" and "administrative" mean nothing to somebody picking from a dropdown.
	// What they need to know is whether areas of this kind can be the answer at all:
	// a fire district can, a city outline only says where you are.
	const areaTypeOptions = useMemo(
		() =>
			(typesData?.jurisdiction_types ?? []).map(areaType => ({
				label:
					areaType.domain === JurisdictionDomain.ADMINISTRATIVE
						? `${areaType.name} — tells us where the address is, never who is in charge`
						: `${areaType.name} — an office that can be in charge`,
				value: areaType.id
			})),
		[typesData]
	);

	const canStart = !!layerName.trim() && !!jurisdictionTypeId && !!layerUrl.trim();

	const handleStartImport = useCallback(async () => {
		if (!canStart) return;
		setIsStarting(true);

		try {
			const { data } = await startImport({
				variables: {
					input: {
						source: layerName.trim(),
						jurisdiction_type_id: jurisdictionTypeId,
						layer_url: layerUrl.trim(),
						name_field: nameField.trim() || undefined,
						id_field: idField.trim() || undefined,
						county_field: countyField.trim() || undefined,
						authority_fields: Object.entries(authorityFields)
							.filter(([, attribute]) => attribute.trim())
							.map(([work_kind, attribute]) => ({ work_kind, attribute: attribute.trim() })),
						effective_from: effectiveFrom ? effectiveFrom.format("YYYY-MM-DD") : undefined
					}
				}
			});

			const result = data?.import_jurisdiction_boundaries;

			console.log("[AHJ import] started:", {
				layer: layerName.trim(),
				jurisdictionTypeId,
				url: layerUrl.trim(),
				importId: result?.import_id ?? null,
				error: result?.error ?? null
			});

			if (!result?.success) {
				message.error(result?.error || "We could not start loading that map. Please try again.");
				return;
			}

			message.success("Loading started. It keeps going even if you close this page.");
			setLayerUrl("");
		} catch (error) {
			console.error("[AHJ import] could not start:", error);
			message.error("We could not start loading that map. Please try again.");
		} finally {
			setIsStarting(false);
		}
	}, [
		canStart,
		startImport,
		layerName,
		jurisdictionTypeId,
		layerUrl,
		nameField,
		idField,
		countyField,
		authorityFields,
		effectiveFrom
	]);

	const columns: TableColumnsType<BoundaryImport> = useMemo(
		() => [
			{
				title: "Map",
				key: "layer",
				render: (_, boundaryImport) => (
					<div className="bly-cell">
						<span className="bly-cell-primary">{boundaryImport.source}</span>
						<span className="bly-cell-secondary">Contains: {boundaryImport.jurisdiction_type?.name}</span>
					</div>
				)
			},
			{
				title: "Progress",
				key: "status",
				width: 240,
				render: (_, boundaryImport) => (
					<div className="bly-cell">
						<Tag color={STATUS_COLOR[boundaryImport.status] ?? "default"}>
							{STATUS_LABEL[boundaryImport.status] ?? boundaryImport.status}
						</Tag>
						{isRunning(boundaryImport.status) && boundaryImport.feature_count > 0 && (
							<span className="bly-cell-secondary">
								{boundaryImport.loaded_count + boundaryImport.unchanged_count} of {boundaryImport.feature_count} areas
								read so far
							</span>
						)}
						{boundaryImport.error_message && <span className="bly-error">{boundaryImport.error_message}</span>}
					</div>
				)
			},
			{
				title: "What came out of this map",
				key: "tally",
				render: (_, boundaryImport) =>
					boundaryImport.status === JurisdictionBoundaryImportStatus.COMPLETE ? (
						<div className="bly-tally">
							{boundaryImport.loaded_count > 0 && (
								<span>
									<strong>{boundaryImport.loaded_count}</strong> areas added or updated
								</span>
							)}
							{boundaryImport.unchanged_count > 0 && (
								<span>
									<strong>{boundaryImport.unchanged_count}</strong> areas were already up to date — nothing to change
								</span>
							)}
							{boundaryImport.superseded_count > 0 && (
								<span>
									<strong>{boundaryImport.superseded_count}</strong> areas changed shape. We kept the old outline so
									past filings still make sense
								</span>
							)}
							{boundaryImport.renumbered_count > 0 && (
								<span>
									<strong>{boundaryImport.renumbered_count}</strong> areas the map renumbered. We recognised them by
									their shape instead
								</span>
							)}
							{boundaryImport.failed_count > 0 && (
								<span className="bly-tally-warn">
									<strong>{boundaryImport.failed_count}</strong> areas could not be read. The reason is next to the
									progress
								</span>
							)}
							{boundaryImport.proposed_count > 0 && (
								<span className="bly-tally-warn">
									<strong>{boundaryImport.proposed_count}</strong> areas need you to confirm which office is in charge
									— go to <strong>Authorities to Confirm</strong>
								</span>
							)}
							{boundaryImport.loaded_count === 0 &&
								boundaryImport.unchanged_count === 0 &&
								boundaryImport.failed_count === 0 && <span>This map had no areas in it.</span>}
							<span className="bly-cell-secondary">Open the row to see every office this map names.</span>
						</div>
					) : (
						<span className="bly-cell-secondary">Still running…</span>
					)
			},
			{
				title: "Loaded on",
				dataIndex: "created_at",
				key: "created_at",
				width: 160,
				render: (createdAt: string) => (
					<span className="bly-cell-secondary">{dayjs(createdAt).format("MMM D, HH:mm")}</span>
				)
			}
		],
		[]
	);

	// Only a finished run has anything to show: the reading is written when the loader
	// works the attributes out, and the offices exist once the areas are in.
	const expandableRows = useMemo(
		() => ({
			rowExpandable: (boundaryImport: BoundaryImport) =>
				boundaryImport.status === JurisdictionBoundaryImportStatus.COMPLETE,
			expandedRowRender: (boundaryImport: BoundaryImport) => <LayerReading boundaryImport={boundaryImport} />,
			// A chevron, like every other thing that opens in this app. The table's own
			// +/- reads as "add one", which is what those mean everywhere else here.
			expandIcon: ({
				expanded,
				onExpand,
				record
			}: {
				expanded: boolean;
				onExpand: (record: BoundaryImport, event: React.MouseEvent<HTMLElement>) => void;
				record: BoundaryImport;
			}) =>
				record.status === JurisdictionBoundaryImportStatus.COMPLETE ? (
					<button
						type="button"
						className="bly-expand"
						aria-label={expanded ? "Hide what this map contains" : "Show what this map contains"}
						onClick={event => onExpand(record, event)}
					>
						{expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
					</button>
				) : null
		}),
		[]
	);

	return (
		<OllieLayout>
			<div className="bly-page">
				<TestingGuide
					title="What this screen is for, in one paragraph"
					summary="When someone starts an inspection, we have to tell them which office reviews it. We work that out from the property's address. To do that we need a map of who is in charge where — and those maps are published by governments, not by us. This screen is where you load one. Until a region has a map loaded here, we guess from the city name in the address and always ask the user to confirm; once it has one, the answer is definite."
					steps={[
						<>
							<strong>Find the map.</strong> Go to the open data portal of the county or city and look for its fire
							districts, fire marshal areas or city limits. Copy the web address that serves it as GeoJSON.
						</>,
						<>
							<strong>Give it a name, say what it draws, paste the address.</strong> Those three fields are all you
							need. Everything else we read from the map itself.
						</>,
						<>
							<strong>Press Load this map.</strong> It appears in the list below and updates on its own. A county-wide
							map takes a few minutes and keeps going if you close this page.
						</>,
						<>
							<strong>Open its row when it finishes.</strong> It shows which column of the map each value came from,
							and every office the map says is in charge. Any office in amber is not in your AHJ list yet — until you
							add it, addresses inside that area cannot be answered.
						</>,
						<>
							<strong>Check it worked.</strong> Start an inspection for a property in that region. The AHJ field should
							fill itself in and say <em>&quot;Selected from the property&apos;s location&quot;</em> instead of asking
							you to confirm.
						</>
					]}
					notMistakes={[
						<>
							<strong>&quot;X areas need you to confirm which office is in charge.&quot;</strong> That is on purpose.
							When a map names an office differently than your AHJ list does, we do not guess — a wrong match sends a
							filing to the wrong desk and nothing on screen would look wrong. A person confirms it in{" "}
							<strong>Authorities to Confirm</strong>.
						</>,
						<>
							<strong>All the optional fields are empty.</strong> Leave them. We read them from the map. They exist only
							to correct a reading you have already seen come out wrong.
						</>,
						<>
							<strong>&quot;N areas were already up to date&quot; and nothing else.</strong> That is a map you loaded
							before and that has not changed. Loading it again is how you stay current, and doing it costs nothing.
						</>
					]}
				/>

				<TestingGuide
					startCollapsed
					title="Where do I find the map for a city or county?"
					summary="You never build these maps — the government that draws the boundary publishes it, and you copy a link. Almost every county and city in the US puts theirs on the same kind of portal, so the hunt is the same every time: search for the layer, open it, and copy the link that serves it as GeoJSON. No terminal, no code."
					steps={[
						<>
							<strong>Search the national catalogue.</strong> Open{" "}
							<a href="https://hub.arcgis.com/search" target="_blank" rel="noreferrer">
								hub.arcgis.com/search
							</a>{" "}
							and type what you are after plus the place. What works:{" "}
							<code>fire marshal Travis County</code>, <code>emergency service districts Williamson County</code>,{" "}
							<code>city limits Pflugerville</code>. On the left, filter <em>Type</em> to <strong>Feature Layer</strong>{" "}
							— those are the ones that can be loaded here.
						</>,
						<>
							<strong>Prefer the office that owns the boundary.</strong> Under each result is who published it. A
							county GIS office or the city itself is the source; a copy someone else re-published may be years old.
							If you see both, take the official one.
						</>,
						<>
							<strong>Open the result and get the link.</strong> On the dataset page press{" "}
							<strong>I want to use this</strong> → <strong>View API Resources</strong>. A panel opens with a{" "}
							<strong>GeoJSON</strong> row. Copy that URL — it is exactly what goes in the field above. It ends in{" "}
							<code>f=geojson</code>.
						</>,
						<>
							<strong>Check it before loading it.</strong> Paste the URL into a new browser tab. You should see a wall
							of text that starts with <code>&#123;&quot;type&quot;:&quot;FeatureCollection&quot;</code>. If instead
							you get a web page, a login, or an error, that link will not work here — go back and take the GeoJSON one.
						</>,
						<>
							<strong>If the county has its own portal.</strong> Many do — search{" "}
							<code>&lt;county&gt; open data</code>. The same <strong>I want to use this</strong> button is there. On
							the handful of portals that look different (Socrata, used by Austin), the button is{" "}
							<strong>Export</strong> → <strong>GeoJSON</strong>.
						</>,
						<>
							<strong>Load it and read the row.</strong> Give it a name, pick what kind of area it draws, paste the
							link, press <strong>Load this map</strong>. When it finishes, open its row: it lists every office the map
							names, and marks in amber the ones missing from your AHJ list. That list is your to-do.
						</>
					]}
					notMistakes={[
						<>
							<strong>One county needs more than one map.</strong> Normal. The fire districts are one map, the city
							limits another, the ETJ a third. Load them one at a time, each with its own name.
						</>,
						<>
							<strong>The map names offices you have never heard of.</strong> Also normal, and it is the point: that
							amber list is exactly which authorities have to be added before addresses in that area can be answered.
						</>,
						<>
							<strong>You cannot find a fire district map for a region.</strong> Then load its city limits instead, as{" "}
							<em>Municipal Boundary</em>. It will not answer who is in charge, but it tells the user which city they
							are in and shows up as context. It is worth having.
						</>
					]}
				/>

				<div className="bly-header">
					<h1 className="bly-title">Jurisdiction Maps</h1>
					<p className="bly-subtitle">
						These are the maps we check an address against to work out which office has authority over it. Each one
						comes from a government portal — a county&apos;s fire districts, a city&apos;s limits — and covers one
						region. Where a region has no map here, we fall back to matching the city name in the address, which is
						usually right but which we always ask the user to confirm.
					</p>
				</div>

				<div className="bly-form">
					<div className="bly-field">
						<label className="bly-label">A name for this map</label>
						<Input
							placeholder="hays_fire_districts"
							value={layerName}
							onChange={event => setLayerName(event.target.value)}
						/>
						<span className="bly-hint">
							Only used here, to tell your maps apart. Load the map again later under this same name and we update
							what it loaded before instead of duplicating it — so write down something you will recognise.
						</span>
					</div>

					<div className="bly-field">
						<label className="bly-label">What kind of area does this map draw?</label>
						<Select
							className="bly-select"
							placeholder="Pick the kind of area"
							value={jurisdictionTypeId}
							options={areaTypeOptions}
							onChange={setJurisdictionTypeId}
							loading={loadingTypes}
						/>
					</div>

					<div className="bly-field">
						<label className="bly-label">Web address of the map data</label>
						<Input
							placeholder="https://…/FeatureServer/0/query?where=1%3D1&outFields=*&f=geojson"
							value={layerUrl}
							onChange={event => setLayerUrl(event.target.value)}
						/>
						<span className="bly-hint">
							Copy it from the government portal that publishes the map. It has to return GeoJSON — on an ArcGIS
							portal that means the address ends in <code>f=geojson</code>.
						</span>
					</div>

					<button type="button" className="bly-advanced-toggle" onClick={() => setAreFieldsShown(shown => !shown)}>
						{areFieldsShown ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
						<span>Correct what we read from the map (you almost never need this)</span>
					</button>

					{areFieldsShown && (
						<div className="bly-advanced">
							<p className="bly-hint">
								<strong>Leave every one of these empty.</strong> We read all of it from the map itself, and once the
								map finishes loading you can see exactly which column each value came from — open its row in the list
								below.
								<br />
								If you type a guess here it replaces a correct reading with a wrong one, and nothing will warn you.
								Only fill one in after you have seen a reading come out wrong.
							</p>
							<div className="bly-advanced-row">
								<div className="bly-field">
									<label className="bly-label">Column with the area&apos;s name</label>
									<Input
										placeholder="we read this from the map"
										value={nameField}
										onChange={event => setNameField(event.target.value)}
									/>
								</div>
								<div className="bly-field">
									<label className="bly-label">Column with the area&apos;s id</label>
									<Input
										placeholder="we read this from the map"
										value={idField}
										onChange={event => setIdField(event.target.value)}
									/>
								</div>
								<div className="bly-field">
									<label className="bly-label">Column with the county</label>
									<Input
										placeholder="we read this from the map"
										value={countyField}
										onChange={event => setCountyField(event.target.value)}
									/>
								</div>
								<div className="bly-field">
									<label className="bly-label">These boundaries apply from</label>
									<DatePicker
										className="bly-select"
										value={effectiveFrom}
										onChange={setEffectiveFrom}
										placeholder="Today"
									/>
								</div>
							</div>

							<p className="bly-hint">
								Some maps name a different office for each kind of permit — one column for building permits, another
								for burn permits. These say which column is which. We read them from the map too, so leave them empty
								unless you have seen one come out wrong.
							</p>
							<div className="bly-advanced-row">
								{WORK_KINDS_WITH_THEIR_OWN_AUTHORITY.map(workKind => (
									<div className="bly-field" key={workKind.value}>
										<label className="bly-label">{workKind.label}</label>
										<Input
											placeholder="we read this from the map"
											value={authorityFields[workKind.value] ?? ""}
											onChange={event =>
												setAuthorityFields(current => ({ ...current, [workKind.value]: event.target.value }))
											}
										/>
									</div>
								))}
							</div>
						</div>
					)}

					<Button
						type="primary"
						className="bly-submit"
						icon={<Download size={16} />}
						disabled={!canStart}
						loading={isStarting}
						onClick={handleStartImport}
					>
						Load this map
					</Button>
				</div>

				<h2 className="bly-section-title">Maps loaded so far</h2>

				{boundaryImports.length === 0 ? (
					<div className="bly-empty">
						<p className="bly-empty-text">
							You have not loaded any map yet, so for every address we fall back to matching the city name and asking the
							user to confirm. Load one above and it appears here while it runs.
						</p>
					</div>
				) : (
					<Table
						className="bly-table"
						rowKey="id"
						columns={columns}
						dataSource={boundaryImports}
						pagination={false}
						expandable={expandableRows}
					/>
				)}
			</div>
		</OllieLayout>
	);
};

export default BoundaryLayers;
